import { useSelector } from "react-redux";
import { useXmlConfiguratorTranslations } from "~/features/configurator/common/useXmlConfiguratorTranslations";
import {
  renderValue,
  SummaryProviderContextProvider,
  Table,
  useSummaryProvider,
} from "~/features/configurator/Shaft/inch/components/ShaftSummaryDataHelper";
import classes from "~/features/configurator/Shaft/inch/components/InchShaftSummaryData.module.less";
import {
  OnStepChangeContextProvider,
  ShaftSummaryStepFrame,
} from "~/features/configurator/Shaft/components/ShaftSummaryStepFrame";
import type { RootState } from "~/app/store";
import { getHighestVisitedStep, selectBaseDesignation } from "~/features/configurator/Linear/redux/selectors";
import { SummaryDataProvider } from "~/features/configurator/Shaft/SummaryDataProvider";
import {
  getStep1SummaryData,
  getStep2SummaryData,
} from "~/features/configurator/Rail/v2/configuration/tkdm/summary/TKDMSummaryDataProvider";
import { Stack, Typography } from "~/common/mui";

interface Props {
  isFullReview: boolean;
  onStepChange?: () => void;
}

export const TKDMRailSummaryData = ({ onStepChange }: Props) => {
  const xmlT = useXmlConfiguratorTranslations().t!;

  const summaryProvider = useSelector((state: RootState) => {
    return new SummaryDataProvider(state.linearConfigurator, xmlT);
  });

  const highestVisitedStep = useSelector(getHighestVisitedStep);

  return (
    <OnStepChangeContextProvider value={onStepChange}>
      <SummaryProviderContextProvider value={summaryProvider}>
        <div className={classes.summaryStepContainer}>
          {highestVisitedStep >= 0 && <Step1Summary />}
          {highestVisitedStep >= 1 && <Step2Summary />}
        </div>
      </SummaryProviderContextProvider>
    </OnStepChangeContextProvider>
  );
};

const Step1Summary = () => {
  const xmlT = useXmlConfiguratorTranslations().t!;
  const summaryProvider = useSummaryProvider();

  const step1Data = getStep1SummaryData(summaryProvider) ?? [];
  const designation = useSelector(selectBaseDesignation);

  return (
    <ShaftSummaryStepFrame stepNumber={1} name={xmlT("configurator.rail.page.1.name")}>
      <Stack gap={1}>
        <Typography variant={"bodyLarge"} sx={{ fontWeight: "bold" }}>
          {designation}
        </Typography>
        <Table>
          {step1Data.map((data) => (
            <tbody key={data.name?.toString()}>
              <tr>
                <td colSpan={2}>
                  <div>{data.name}</div>
                </td>
                <td colSpan={2}>
                  <div>{renderValue(data)}</div>
                </td>
                <td colSpan={2} />
              </tr>
            </tbody>
          ))}
        </Table>
      </Stack>
    </ShaftSummaryStepFrame>
  );
};

const Step2Summary = () => {
  const xmlT = useXmlConfiguratorTranslations().t!;
  const summaryProvider = useSummaryProvider();

  const step1Data = getStep2SummaryData(summaryProvider) ?? [];

  return (
    <ShaftSummaryStepFrame stepNumber={2} name={xmlT("configurator.rail.page.4.name")}>
      <Stack gap={1}>
        <Table>
          {step1Data.map((data) => (
            <tbody key={data.name?.toString()}>
              <tr>
                <td colSpan={2} dangerouslySetInnerHTML={{ __html: data.name }} />
                <td colSpan={2}>
                  <div>{renderValue(data)}</div>
                </td>
                <td colSpan={2} />
              </tr>
            </tbody>
          ))}
        </Table>
      </Stack>
    </ShaftSummaryStepFrame>
  );
};
